import type { VoidFunctionComponent } from 'react';

import Icon, { icons } from '../../generic/Icon';
import { NEW_TAB_PROPS } from '../../generic/Link';
import { Menu, MenuButton, MenuItem } from '../../generic/Menu';

import styles from './FossabotPrRowActions.module.less';

type Props = {
  title: string;
  vcsUrl: string | null;
  fossabotUrl: string | null;
};

/**
 * The menu on the right of a PR row, holding the two ways out to the PR itself.
 * Both links are real anchors, so they can be opened in a new tab, copied or
 * middle-clicked like any other link.
 *
 * Each link is dropped when its URL is missing, and a PR missing both gets no
 * menu at all: the row leaves this out rather than offering a control that opens
 * on nothing. A blank URL counts as missing, since the upstream schema parses
 * these as plain nullable strings and an empty href would only reopen this page.
 *
 * A MenuButton rather than a bare button: only that component carries the
 * aria-haspopup and aria-expanded wiring that says the control opens a menu and
 * whether it is open, and it is a real button, so it is reachable by tab and
 * opens on Enter and Space.
 *
 * The glyph is hidden from assistive tech like every other Icon, so the button's
 * name has to come from a label, and that label carries the PR's title: a table
 * of these is otherwise a column of identically named buttons. The popup is
 * named separately because it only borrows the button's text when that text is a
 * single string, and this button holds nothing but the glyph.
 */
export const FossabotPrRowActions: VoidFunctionComponent<Props> = ({
  title,
  vcsUrl,
  fossabotUrl,
}) => {
  const label = `Actions for ${title}`;

  return (
    <Menu
      aria-label={label}
      direction="bottom"
      align="end"
      menuButton={(
        <MenuButton aria-label={label} className={styles.actionsButton}>
          <Icon icon={icons.ellipsisHorizontal} />
        </MenuButton>
      )}
    >
      {vcsUrl && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <MenuItem href={vcsUrl} {...NEW_TAB_PROPS}>
          Review on GitHub
        </MenuItem>
      )}
      {fossabotUrl && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <MenuItem href={fossabotUrl} {...NEW_TAB_PROPS}>
          View in fossabot
        </MenuItem>
      )}
    </Menu>
  );
};
